'use client'

import DateRangeFilter from '@/components/date-range-filter'
import type { ServiceLabels } from '@/lib/form-state/util'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { FilterField, ListFilterBar } from '@/components/list-page'
import { getConsultationsByCompany } from '@/lib/api/consultations/consultation-api'
import { getExamsByCompany } from '@/lib/api/exams/exam-api'
import { getOrdersByCompany } from '@/lib/api/orders/order-api'
import {
    BILLING_STATUS_BADGE,
    BILLING_STATUS_LABELS,
    PAYMENT_METHOD_LABELS,
    PAYMENT_METHOD_ORDER,
} from '@/lib/billing/payment-labels'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {
    MdArrowBack,
    MdBiotech,
    MdCheckCircle,
    MdMedicalServices,
    MdPointOfSale,
    MdReceiptLong,
    MdSchedule,
    MdVaccines,
    MdVisibility,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

/**
 * Fechamento de caixa do operador.
 *
 * NÃO é um relatório do sistema: mostra apenas o que o USUÁRIO LOGADO vendeu
 * (`createdById` fixo no próprio usuário, sem seletor de vendedor) no período
 * escolhido. É de propósito — os relatórios completos ficam em Relatórios, com
 * permissão própria; aqui o operador confere o próprio caixa sem enxergar o
 * movimento da empresa.
 *
 * Sem endpoint novo: reusa as três listagens existentes, que já aceitam
 * `createdById` + período e já devolvem a cobrança. Isso evita criar uma
 * superfície nova de dados agregados.
 */

type AppointmentKind = 'CONSULTATION' | 'EXAM' | 'PROCEDURE'

/** Rótulos no vocabulário do segmento (fora da saúde, Procedimento vira Pedido). */
function buildKindLabels(labels: ServiceLabels): Record<AppointmentKind, string> {
    return {
        CONSULTATION: labels.Consultation,
        EXAM: labels.Exam,
        PROCEDURE: labels.Order,
    }
}

const KIND_ROUTES: Record<AppointmentKind, string> = {
    CONSULTATION: '/bookings/consultations',
    EXAM: '/bookings/exams',
    PROCEDURE: '/bookings/orders',
}

const KIND_ICONS: Record<AppointmentKind, ReactNode> = {
    CONSULTATION: <MdMedicalServices className="text-base" />,
    EXAM: <MdBiotech className="text-base" />,
    PROCEDURE: <MdVaccines className="text-base" />,
}

/**
 * Qual data o período filtra.
 *
 * `PAID` é o padrão porque é o que fecha caixa: o turno confere o DINHEIRO QUE
 * ENTROU, e a cobrança criada na sexta e paga na segunda pertence ao caixa da
 * segunda. Com o filtro por registro ela não apareceria em dia nenhum útil — na
 * sexta ainda não havia pagamento, e na segunda o registro é antigo.
 *
 * `CREATED` continua disponível para a outra pergunta ("o que eu vendi hoje",
 * pago ou não), que é a que a tela respondia antes.
 */
type DateBasis = 'PAID' | 'CREATED'

const DATE_BASIS_LABELS: Record<DateBasis, string> = {
    PAID: 'Data de pagamento',
    CREATED: 'Data do registro',
}

/** Ordem dos status no select — o ciclo normal da cobrança primeiro. */
const BILLING_STATUS_ORDER = [
    'PENDING',
    'PAID',
    'PARTIALLY_PAID',
    'OVERDUE',
    'CANCELLED',
    'REFUNDED',
    'PARTIALLY_REFUNDED',
    'IN_DISPUTE',
    'WRITTEN_OFF',
]

/** Linha normalizada: as três origens viram a mesma forma. */
interface Row {
    kind: AppointmentKind
    id: number
    title: string
    clientName: string
    date: string | null
    /** Quando a cobrança foi paga. `null` = ainda não foi. */
    paidAt: string | null
    /** Valor efetivamente cobrado (o da cobrança, não o do catálogo). */
    amount: number
    paidAmount: number
    billingStatus: string | null
    paymentMethod: string | null
}

const formatCents = (cents: number): string =>
    (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

/**
 * A fatia DESTE atendimento na cobrança, quando ela cobre mais de um.
 *
 * ⚠️ Sem isto o caixa MULTIPLICA. O paciente que marca exame em dois laboratórios
 * e uma consulta pode pagar tudo numa cobrança só de R$ 900; ela fica pendurada nos
 * três atendimentos, e cada linha leria R$ 900 — o dia fecharia em R$ 2.700.
 * Ver docs/billing-payout-share.md.
 */
function shareOf(
    kind: AppointmentKind,
    id: number,
    billing: any
): { amount: number } | null {
    const shares = billing?.payoutShares
    if (!shares?.length || shares.length <= 1) return null
    const share = shares.find(
        (s: any) => s.sourceType === kind && s.sourceId === id
    )
    return share?.amount != null ? { amount: share.amount } : null
}

/** Parte de `total` proporcional ao peso da fatia na cobrança. */
function proporcional(total: number, parte: number, todo: number): number {
    if (!todo || !total) return 0
    return Math.round((total * parte) / todo)
}

/**
 * Converte um atendimento (de qualquer um dos três tipos) na linha do relatório.
 * `scheduledAt` é a data do atendimento; `requestedAt`/`createdAt` cobrem os que
 * ainda não têm agenda marcada.
 */
function toRow(
    kind: AppointmentKind,
    item: any,
    kindLabels: Record<AppointmentKind, string>
): Row {
    const billing = item.billing || null
    const share = shareOf(kind, item.id, billing)
    return {
        kind,
        id: item.id,
        title: item.title || `${kindLabels[kind]} #${item.id}`,
        clientName: item.client?.name || '—',
        date: item.scheduledAt || item.requestedAt || item.createdAt || null,
        paidAt: billing?.paidAt ?? null,
        amount: share?.amount ?? billing?.amount ?? 0,
        // Pago: na cobrança compartilhada o pagamento é único e cobre todos, então
        // a parte deste atendimento é proporcional ao que ele representa no total.
        paidAmount: share
            ? proporcional(billing?.paidAmount ?? 0, share.amount, billing?.amount ?? 0)
            : (billing?.paidAmount ?? 0),
        billingStatus: billing?.status ?? null,
        paymentMethod: billing?.paymentMethod ?? null,
    }
}

export default function CashClosingPage() {
    const kindLabels = buildKindLabels(useServiceLabel())
    const navigate = useNavigate()
    const { user } = useAuth()

    const [loading, setLoading] = useState(true)
    const [rows, setRows] = useState<Row[]>([])
    /**
     * `null` = o período ainda não chegou do filtro.
     *
     * O `DateRangeFilter` emite a janela do preset inicial no efeito de
     * montagem, e efeitos de filho rodam antes dos do pai: carregar já no
     * primeiro commit disparava uma busca SEM período (o caixa inteiro) que
     * corria em paralelo com a busca certa — e, por ser muito maior, costumava
     * responder depois e sobrescrever o resultado filtrado.
     */
    const [dateRange, setDateRange] = useState<{
        startDate?: string
        endDate?: string
    } | null>(null)
    const [dateBasis, setDateBasis] = useState<DateBasis>('PAID')
    const [kindFilter, setKindFilter] = useState<AppointmentKind | ''>('')
    const [statusFilter, setStatusFilter] = useState('')
    const [methodFilter, setMethodFilter] = useState('')

    const companyId = user?.companyId
    const sellerId = user?.id
    const startDate = dateRange?.startDate
    const endDate = dateRange?.endDate
    const rangeReady = dateRange !== null

    /** Descarta resposta de uma busca que já foi substituída por outra. */
    const requestSeq = useRef(0)

    const load = useCallback(async () => {
        if (!companyId || !sellerId || !rangeReady) return

        const seq = ++requestSeq.current
        setLoading(true)
        try {
            // Sem `page`: o backend devolve o período inteiro. Um fechamento é de
            // um vendedor num intervalo curto — paginar aqui só esconderia parte
            // do caixa e quebraria os totais.
            //
            // ⚠️ As duas janelas são EXCLUDENTES, nunca somadas. Mandar as duas
            // faria interseção, e o caso que motivou o filtro — venda de sexta paga
            // hoje — sairia justamente do resultado: ela é de hoje pelo pagamento e
            // de sexta pelo registro.
            const params = {
                createdById: sellerId,
                ...(dateBasis === 'PAID'
                    ? { paidStartDate: startDate, paidEndDate: endDate }
                    : { startDate, endDate }),
            }

            const [consultations, exams, orders] = await Promise.all([
                getConsultationsByCompany(companyId, params),
                getExamsByCompany(companyId, params),
                getOrdersByCompany(companyId, params),
            ])

            if (seq !== requestSeq.current) return

            const merged: Row[] = [
                ...(consultations.success
                    ? (consultations.result || []).map(c =>
                          toRow('CONSULTATION', c, kindLabels)
                      )
                    : []),
                ...(exams.success
                    ? (exams.result || []).map(e => toRow('EXAM', e, kindLabels))
                    : []),
                ...(orders.success
                    ? (orders.result || []).map(p => toRow('PROCEDURE', p, kindLabels))
                    : []),
            ]

            // Ordena pela data que o operador escolheu como base — no fechamento
            // por pagamento, ordenar por registro embaralharia a conferência.
            merged.sort((a, b) => {
                const field = dateBasis === 'PAID' ? 'paidAt' : 'date'
                const da = a[field] ? new Date(a[field]!).getTime() : 0
                const db = b[field] ? new Date(b[field]!).getTime() : 0
                return db - da
            })

            setRows(merged)
        } catch {
            if (seq === requestSeq.current) {
                toast.error('Erro ao carregar o fechamento de caixa')
            }
        } finally {
            if (seq === requestSeq.current) setLoading(false)
        }
    }, [companyId, sellerId, rangeReady, startDate, endDate, dateBasis])

    useEffect(() => {
        load()
    }, [load])

    // Tipo, status e forma de pagamento filtram em memória: os dados do período
    // já estão carregados, e refazer as três requisições a cada clique não
    // traria nada de novo.
    const visibleRows = useMemo(
        () =>
            rows.filter(r => {
                // Filtrando por pagamento, quem não foi pago não é caixa. Normalmente
                // o backend já garante isso; a exceção é o "Personalizado" com só uma
                // data preenchida, que emite janela vazia — sem esta linha, escolher o
                // período personalizado despejaria os não pagos na conferência.
                if (dateBasis === 'PAID' && !r.paidAt) return false
                if (kindFilter && r.kind !== kindFilter) return false
                if (statusFilter && r.billingStatus !== statusFilter)
                    return false
                if (methodFilter && r.paymentMethod !== methodFilter)
                    return false
                return true
            }),
        [rows, dateBasis, kindFilter, statusFilter, methodFilter]
    )

    /**
     * O que fecha o caixa: quanto foi vendido e quanto entrou de fato.
     * `recebido` soma `paidAmount` (parcial conta o que já entrou); `aReceber` é
     * o que falta das cobranças que ainda podem ser pagas — cancelada e
     * estornada não entram, senão o operador cobraria de novo.
     */
    const totals = useMemo(() => {
        const OPEN_STATUSES = [
            'PENDING',
            'OVERDUE',
            'PARTIALLY_PAID',
            'IN_DISPUTE',
        ]
        return visibleRows.reduce(
            (acc, r) => {
                acc.count += 1
                acc.total += r.amount
                acc.recebido += r.paidAmount
                if (r.billingStatus && OPEN_STATUSES.includes(r.billingStatus)) {
                    acc.aReceber += Math.max(0, r.amount - r.paidAmount)
                }
                return acc
            },
            { count: 0, total: 0, recebido: 0, aReceber: 0 }
        )
    }, [visibleRows])

    return (
        <div className="flex flex-col gap-5">
            <header className="relative overflow-hidden rounded-2xl border border-base-300 bg-base-100">
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-linear-to-r from-success/10 via-primary/5 to-transparent"
                />
                <div className="relative flex items-center gap-4 p-5">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="btn btn-ghost btn-circle btn-sm shrink-0"
                        title="Voltar"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-success/10 text-success ring-1 ring-success/20">
                        <MdPointOfSale className="text-2xl" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium uppercase tracking-wide text-base-content/40">
                            Fechamento de caixa
                        </p>
                        <h1 className="truncate text-xl font-bold text-base-content sm:text-2xl">
                            {user?.name || 'Meu caixa'}
                        </h1>
                        <p className="text-sm text-base-content/50">
                            {dateBasis === 'PAID'
                                ? 'Seus atendimentos pagos no período'
                                : 'Seus atendimentos registrados no período'}
                        </p>
                    </div>
                </div>
            </header>

            <ListFilterBar>
                {/* Fechamento é operação de fim de turno: abre no dia corrente. */}
                {/* Qual data a janela ao lado considera. O padrão é o pagamento —
                    é o dinheiro que entrou que fecha o caixa do turno. */}
                <FilterField label="Filtrar por" htmlFor="cash-date-basis">
                    <select
                        id="cash-date-basis"
                        className="select select-bordered select-sm w-48"
                        value={dateBasis}
                        onChange={e =>
                            setDateBasis(e.target.value as DateBasis)
                        }
                    >
                        {(Object.keys(DATE_BASIS_LABELS) as DateBasis[]).map(
                            b => (
                                <option key={b} value={b}>
                                    {DATE_BASIS_LABELS[b]}
                                </option>
                            )
                        )}
                    </select>
                </FilterField>

                {/* O rótulo acompanha a base: sem isso o campo diz "Período" e o
                    operador não sabe se "Ontem" é a venda de ontem ou o dinheiro
                    que entrou ontem. Os presets (Hoje, Ontem, Esta semana, Este
                    mês, Personalizado) são os do próprio componente. */}
                <DateRangeFilter
                    label={
                        dateBasis === 'PAID' ? 'Pago em' : 'Registrado em'
                    }
                    onChange={setDateRange}
                    defaultPreset="today"
                />

                <FilterField label="Tipo" htmlFor="cash-kind-filter">
                    <select
                        id="cash-kind-filter"
                        className="select select-bordered select-sm w-44"
                        value={kindFilter}
                        onChange={e =>
                            setKindFilter(e.target.value as AppointmentKind | '')
                        }
                    >
                        <option value="">Todos os tipos</option>
                        {(Object.keys(kindLabels) as AppointmentKind[]).map(
                            k => (
                                <option key={k} value={k}>
                                    {kindLabels[k]}
                                </option>
                            )
                        )}
                    </select>
                </FilterField>

                <FilterField
                    label="Status da cobrança"
                    htmlFor="cash-status-filter"
                >
                    <select
                        id="cash-status-filter"
                        className="select select-bordered select-sm w-48"
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                    >
                        <option value="">Todos os status</option>
                        {BILLING_STATUS_ORDER.map(s => (
                            <option key={s} value={s}>
                                {BILLING_STATUS_LABELS[s] || s}
                            </option>
                        ))}
                    </select>
                </FilterField>

                <FilterField
                    label="Forma de pagamento"
                    htmlFor="cash-method-filter"
                >
                    <select
                        id="cash-method-filter"
                        className="select select-bordered select-sm w-48"
                        value={methodFilter}
                        onChange={e => setMethodFilter(e.target.value)}
                    >
                        <option value="">Todas as formas</option>
                        {PAYMENT_METHOD_ORDER.map(m => (
                            <option key={m} value={m}>
                                {PAYMENT_METHOD_LABELS[m] || m}
                            </option>
                        ))}
                    </select>
                </FilterField>
            </ListFilterBar>

            {/* Totais: é o que fecha o caixa, então ganham peso e cor própria. */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {[
                    {
                        label: 'Atendimentos',
                        value: String(totals.count),
                        icon: <MdReceiptLong className="text-lg" />,
                        tone: 'text-base-content',
                        ring: 'bg-base-200 text-base-content/50',
                    },
                    {
                        label: 'Total vendido',
                        value: formatCents(totals.total),
                        icon: <MdPointOfSale className="text-lg" />,
                        tone: 'text-base-content',
                        ring: 'bg-base-200 text-base-content/50',
                    },
                    {
                        label: 'Recebido',
                        value: formatCents(totals.recebido),
                        icon: <MdCheckCircle className="text-lg" />,
                        tone: 'text-success',
                        ring: 'bg-success/10 text-success',
                    },
                    {
                        label: 'A receber',
                        value: formatCents(totals.aReceber),
                        icon: <MdSchedule className="text-lg" />,
                        tone: 'text-warning',
                        ring: 'bg-warning/10 text-warning',
                    },
                ].map(card => (
                    <div
                        key={card.label}
                        className="flex items-center gap-3 rounded-2xl border border-base-300 bg-base-100 p-4"
                    >
                        <span
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${card.ring}`}
                        >
                            {card.icon}
                        </span>
                        <div className="min-w-0">
                            <p className="text-xs text-base-content/50">
                                {card.label}
                            </p>
                            <p
                                className={`truncate text-lg font-bold ${card.tone}`}
                            >
                                {card.value}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Lista */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            ) : visibleRows.length === 0 ? (
                <div className="rounded-2xl border border-base-300 bg-base-100 p-12 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-base-200 text-3xl text-base-content/30">
                        <MdPointOfSale />
                    </div>
                    <p className="text-sm text-base-content/50">
                        {dateBasis === 'PAID'
                            ? 'Nenhum atendimento seu foi pago no período selecionado.'
                            : 'Nenhum atendimento seu no período selecionado.'}
                    </p>
                </div>
            ) : (
                <div className="overflow-hidden rounded-2xl border border-base-300 bg-base-100">
                    {/* `table-fixed` + truncate no lugar de rolagem horizontal: a
                        coluna de ações é a mais importante da linha e era
                        justamente a que ficava fora da tela. Colunas de apoio
                        somem antes disso, por breakpoint. */}
                    <table className="table table-sm table-fixed w-full">
                        <thead>
                            <tr>
                                <th className="w-[34%]">Atendimento</th>
                                <th className="hidden w-[22%] md:table-cell">
                                    Paciente
                                </th>
                                {/* A coluna acompanha a base escolhida: conferir o
                                    caixa por pagamento e ver a data do registro na
                                    linha faria o operador duvidar do filtro. */}
                                <th className="hidden w-[13%] sm:table-cell">
                                    {dateBasis === 'PAID'
                                        ? 'Pago em'
                                        : 'Registro'}
                                </th>
                                <th className="w-[15%] text-right">Valor</th>
                                <th className="w-[18%]">Cobrança</th>
                                <th className="w-16 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map(r => (
                                <tr key={`${r.kind}-${r.id}`} className="hover">
                                    <td className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-base-200 text-base-content/50"
                                                title={kindLabels[r.kind]}
                                            >
                                                {KIND_ICONS[r.kind]}
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span
                                                    className="block truncate font-medium"
                                                    title={r.title}
                                                >
                                                    {r.title}
                                                </span>
                                                {/* Em telas estreitas o paciente
                                                    e o tipo entram aqui, já que
                                                    as colunas deles somem. */}
                                                <span className="block truncate text-xs text-base-content/50">
                                                    {kindLabels[r.kind]}
                                                    <span className="md:hidden">
                                                        {' · '}
                                                        {r.clientName}
                                                    </span>
                                                </span>
                                            </span>
                                        </div>
                                    </td>
                                    <td
                                        className="hidden truncate md:table-cell"
                                        title={r.clientName}
                                    >
                                        {r.clientName}
                                    </td>
                                    <td className="hidden whitespace-nowrap sm:table-cell">
                                        {(() => {
                                            const shown =
                                                dateBasis === 'PAID'
                                                    ? r.paidAt
                                                    : r.date
                                            return shown
                                                ? format(
                                                      new Date(shown),
                                                      dateBasis === 'PAID'
                                                          ? 'dd/MM HH:mm'
                                                          : 'dd/MM/yyyy'
                                                  )
                                                : '—'
                                        })()}
                                    </td>
                                    <td className="whitespace-nowrap text-right font-semibold">
                                        {formatCents(r.amount)}
                                    </td>
                                    <td className="min-w-0">
                                        {r.billingStatus ? (
                                            <span className="flex min-w-0 flex-col items-start gap-1">
                                                <span
                                                    className={`badge badge-sm max-w-full ${
                                                        BILLING_STATUS_BADGE[
                                                            r.billingStatus
                                                        ] || 'badge-ghost'
                                                    }`}
                                                >
                                                    <span className="truncate">
                                                        {BILLING_STATUS_LABELS[
                                                            r.billingStatus
                                                        ] || r.billingStatus}
                                                    </span>
                                                </span>
                                                {r.paymentMethod && (
                                                    <span className="w-full truncate text-xs text-base-content/50">
                                                        {PAYMENT_METHOD_LABELS[
                                                            r.paymentMethod
                                                        ] || r.paymentMethod}
                                                    </span>
                                                )}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-base-content/40">
                                                Sem cobrança
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-right">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                navigate(
                                                    `${KIND_ROUTES[r.kind]}/${r.id}`
                                                )
                                            }
                                            className="btn btn-ghost btn-xs btn-square"
                                            title="Ver detalhes do atendimento"
                                            aria-label="Ver detalhes do atendimento"
                                        >
                                            <MdVisibility className="text-base" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
